"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchApi } from "@local/utils/src/fetchApi";
import { rebuildUrlParams } from "@local/utils/src/rebuildUrlParams";
import type { Cart } from "@local/cart/src/cart";
import { useSearchParams } from "next/navigation";
import { getUpsellIdFallback } from "./upsellFallback";
import { persistUpsellChannel, upsellStorageKey } from "@local/tracking/src/upsellChannelStorage";
interface Props {
  channel: string;
  market: string;
}
/**
 * Custom hook for managing upsell functionality based on cart state
 * @param props - Configuration object
 * @param props.channel - The sales channel identifier
 * @param props.market - The market identifier
 * @returns Object containing upsellId and upsellUrl
 */
export const useUpsell = ({
  channel,
  market
}: Props) => {
  const searchParams = useSearchParams();
  const storageKey = upsellStorageKey(market, channel);

  // Start as null on both server and client to avoid SSR hydration mismatch.
  // Effect below hydrates from localStorage after mount — this setState in an
  // effect is intentional (external-store hydration), not derived state.
  const [upsellId, setUpsellId] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedId = window.localStorage.getItem(storageKey);
    if (storedId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUpsellId(storedId);
    }
  }, [storageKey]);
  const getCartMostExpensiveProductAndType = (event: Cart.CartEvent) => {
    try {
      const cart = event.detail.state;
      if (!cart) return [null, null];
      const sortedProducts = [...cart.items].filter(item => !item.parentItemId).sort((a, b) => b.price - a.price);
      const mostExpensiveProduct = sortedProducts[0];
      return [mostExpensiveProduct.listingId, sortedProducts.length > 1 ? "multi" : "single"];
    } catch (_) {
      // silently error
      return [null, null];
    }
  };
  const upsellHandler = useCallback(async (event_0: Event) => {
    try {
      const cartEvent = event_0 as Cart.CartEvent;

      // Get most expensive product and type from cart
      const [mostExpensiveProduct_0, type] = getCartMostExpensiveProductAndType(cartEvent);

      // If no most expensive product, return
      if (!mostExpensiveProduct_0 || !type) return;

      // If the most expensive product is the same as the current upsell, return
      if (mostExpensiveProduct_0 === upsellId) return;
      let foundUpsellId: string | null = null;

      // Get upsell for most expensive product
      const upsellRequest = await fetchApi<string>({
        endpoint: "/api/upsell/get",
        method: "POST",
        data: {
          channel: channel,
          typeId: type,
          listingId: mostExpensiveProduct_0,
          market
        }
      });
      foundUpsellId = upsellRequest || getUpsellIdFallback(market, channel);
      if (!foundUpsellId || foundUpsellId === "") return;

      // Save upsell ID to cart storage
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          localStorage.setItem(storageKey, foundUpsellId);
          // Bridge the originating channel to the upsell page — the
          // /up/<id> route has no channel segment and TrackingLoader
          // would otherwise fall back to "store".
          persistUpsellChannel(foundUpsellId, channel);
          setUpsellId(foundUpsellId);
        }
      } catch (error_0) {
        console.error(error_0);
      }
    } catch (error) {
      console.error("Upsell handler error:", error);
    }
  }, [channel, market, upsellId, storageKey]);

  // Listen to cart events
  useEffect(() => {
    window?.addEventListener?.("cart-updated", upsellHandler);
    return () => {
      window?.removeEventListener?.("cart-updated", upsellHandler);
    };
  }, [upsellHandler]);
  const upsellUrl = useMemo(() => {
    if (!upsellId) {
      return null;
    }
    return rebuildUrlParams(searchParams, `/${market}/up/${upsellId}`);
  }, [upsellId, market, searchParams]);

  // Build URL for upsell
  return {
    upsellId,
    upsellUrl
  };
};